// Landing page content configuration - persona-driven copy
export const LANDING_CONTENT = {
  retail: {
    hero: {
      headline: 'Clarity for every investment decision.',
      subheadline: 'Track your portfolio, understand diversification, align goals, and invest with discipline—without noise.',
      primaryCTA: 'View My Portfolio',
      secondaryCTA: 'Explore Model Portfolios',
      trustLine: 'SEBI-Registered Advisors. Transparent Analytics. No Commissions.'
    },
    valueCards: [
      {
        title: 'Portfolio at a glance',
        description: 'Track performance, holdings, and diversification across all your investments in one place.',
        icon: 'pi-chart-line'
      },
      {
        title: 'Expert guidance',
        description: 'Access dedicated advisors and personalized concierge support for your investment journey.',
        icon: 'pi-users'
      },
      {
        title: 'Better decisions',
        description: 'Research-backed screeners, valuation models, and global investing options for confident investing.',
        icon: 'pi-lightbulb'
      }
    ],
    faq: [
      {
        question: 'How does AssetNivesh help me invest better?',
        answer: 'We provide transparent portfolio analytics, goal tracking, and research tools to help you make informed decisions aligned with your risk profile and financial goals.'
      },
      {
        question: 'Is my portfolio data secure?',
        answer: 'Yes. We use bank-grade encryption and never share your data with third parties. You control what data you upload and can delete it anytime.'
      }
    ]
  },
  advisor: {
    hero: {
      headline: 'A client-ready wealth platform—built for advisors.',
      subheadline: 'Deliver structured portfolios, explainable recommendations, and ongoing monitoring—at scale.',
      primaryCTA: 'Explore Advisor Workspace',
      secondaryCTA: 'Run Client Scenarios',
      trustLine: 'Repeatable process. Clear rationale. Better client confidence.'
    },
    valueCards: [
      {
        title: 'Model portfolios by risk profile',
        description: 'Deliver consistent, explainable asset allocations aligned with client risk tolerance and goals.',
        icon: 'pi-briefcase'
      },
      {
        title: 'Client monitoring & insights',
        description: 'Track performance attribution, rebalancing needs, and portfolio drift for all clients in one dashboard.',
        icon: 'pi-users'
      },
      {
        title: 'Research & education content',
        description: 'Share curated insights and educational content to help clients stay invested through market cycles.',
        icon: 'pi-book'
      }
    ],
    faq: [
      {
        question: 'How does AssetNivesh help me serve clients better?',
        answer: 'We provide advisor-focused tools for portfolio construction, client monitoring, and performance reporting—all with explainable rationale you can share with clients.'
      },
      {
        question: 'Can I manage multiple client portfolios?',
        answer: 'Yes. Our platform supports multi-client management with individual risk profiles, goals, and custom portfolio allocations for each client.'
      }
    ]
  },
  common: {
    howItWorks: [
      {
        step: 1,
        title: 'Risk Profile Assessment',
        description: 'Understand your risk tolerance through a structured questionnaire aligned with your goals and time horizon.'
      },
      {
        step: 2,
        title: 'Setting Goals',
        description: 'Define clear financial objectives with timelines and target amounts for systematic wealth creation.'
      },
      {
        step: 3,
        title: 'Portfolio Construction',
        description: 'Build a diversified portfolio using model allocations, research tools, and valuation-led stock selection.'
      },
      {
        step: 4,
        title: 'Monitoring & Rebalancing',
        description: 'Track returns, attribution, and portfolio health with regular insights and rebalancing alerts to stay on course.'
      }
    ],
    capabilities: [
      {
        title: 'AI-Powered Portfolio Analysis',
        description: 'Agentic AI transforms portfolio analytics into actionable, context-aware investment guidance',
        icon: 'pi-sparkles'
      },
      {
        title: 'Model Portfolios',
        description: 'Pre-built allocations by risk profile',
        icon: 'pi-th-large'
      },
      {
        title: 'Stock Analysis with Valuation',
        description: 'DCF, P/B models and intrinsic value analysis',
        icon: 'pi-chart-bar'
      },
      {
        title: 'Global Investing',
        description: 'Expert guidance and portfolio tracking for GIFT City, UDRs, Feeder Funds, and Direct Equity',
        icon: 'pi-globe'
      },
      {
        title: 'Portfolio Analytics',
        description: 'Attribution, diversification, and risk metrics',
        icon: 'pi-chart-pie'
      },
      {
        title: 'SEBI-Compliant Advisory',
        description: 'Registered investment advisers, fiduciary standards',
        icon: 'pi-shield'
      },
      {
        title: 'Mutual Fund Recommender',
        description: 'Category-wise fund selection tools',
        icon: 'pi-filter'
      },
      {
        title: 'Account Aggregator',
        description: 'RBI-regulated secure portfolio import',
        icon: 'pi-link'
      },
      {
        title: 'Personalized Concierge Support',
        description: 'Dedicated advisors for ongoing guidance',
        icon: 'pi-users'
      }
    ],
    expandableCapabilities: [
      {
        title: 'Buy on Zerodha',
        description: 'Seamless redirect to execute trades on Zerodha'
      },
      {
        title: 'Top Dashboard Insights',
        description: 'Goal tracking, SIP status, and portfolio health at a glance'
      },
      {
        title: 'Investment Vehicles',
        description: 'Compare stocks, MFs, bonds, and alternatives'
      },
      {
        title: 'Educational Content',
        description: 'Blogs, videos, and investment guides'
      }
    ],
    faqCommon: [
      {
        question: 'What makes AssetNivesh different from other platforms?',
        answer: 'We focus on transparency and explainability. Every recommendation comes with clear rationale, and we never earn commissions from product sales—only transparent advisory fees.'
      },
      {
        question: 'Are you SEBI registered?',
        answer: 'Yes, AssetNivesh operates as a SEBI-registered investment adviser. We follow regulatory guidelines and fiduciary standards in all our recommendations.'
      },
      {
        question: 'What are the costs involved?',
        answer: 'We offer tiered pricing based on portfolio size and features needed. There are no hidden commissions—only transparent subscription or advisory fees. See pricing section for details.'
      }
    ]
  }
};

// Color palette from existing portal
export const LANDING_COLORS = {
  primary: '#3b82f6',
  primaryDark: '#2563eb',
  secondary: '#10b981',
  accent: '#f59e0b',
  text: '#1f2937',
  textLight: '#6b7280',
  bg: '#ffffff',
  bgLight: '#f9fafb',
  border: '#e5e7eb'
};

// Spacing scale (8px base)
export const SPACING = {
  xs: '8px',
  sm: '16px',
  md: '24px',
  lg: '32px',
  xl: '48px',
  xxl: '64px'
};
